import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography
} from '@mui/material';
import { useMutation,useQueries } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AttToken from '../../../../helpers/attToken';
import Checklists from '../../../../service/Checklists';
import { ConjuntoType } from '../../../../service/Places';
import PlanoDeAcao from '../../../../service/PlanoDeAcao';
import queryClient from '../../../../service/query';
import GenericAutoComplete from '../../../GenericAutoComplete';

type props = {
  openModalPA: boolean,
  setOpenModalPA: Function,
  conjunto: ConjuntoType,
};

const PAService = new PlanoDeAcao();
const formService = new Checklists();

function AssociateForm({
  users,
  label,
  forms,
  selectedValues,
  setUsers,
  selectedForms }:
  {
    users: { nome: string, id: string }[],
    label: string,
    forms: { nome: string, id: string }[],
    selectedValues: string[],
    setUsers: Function,
    selectedForms: { [name: string]: string[] },
  }) {
  const { t } = useTranslation('translation');
  return (
    <>
      <GenericAutoComplete
        label={label}
        options={users?.map((user: { id: string, nome: string }) => ({ value: user.id, label: user.nome })) ?? []}
        selectedValues={selectedValues ?? []}
        onChange={(values) => setUsers((prev: { [name: string]: string[] }) => {
          const newUsers = { ...prev };
          Object.keys(prev)?.forEach((key) => {
            if (prev[key as keyof typeof prev]?.length === 0) {
              delete newUsers[key as keyof typeof newUsers];
            }
          });
          values.forEach((value: string) => {
            if (!Object.keys(newUsers)?.includes(value)) {
              newUsers[value] = [];
            }
          });
          return newUsers;
        })}
      />
      {selectedValues.length > 0
        && <>
          <Divider variant='middle' sx={{ marginTop: '10px', marginBottom: '10px' }} />
          <Typography variant='subtitle2' color='textDisabled' fontWeight={600}>
            {t('Services.ServiceModal.associatedForms')}
          </Typography>
          <Divider variant='middle' sx={{ marginTop: '10px', marginBottom: '10px' }} />
        </>}
      {selectedValues?.map((user, index) => (
        <GenericAutoComplete
          key={index}
          label={user}
          options={forms?.map((form: { id: string, nome: string }) => ({ value: form.id, label: form.nome })) ?? []}
          selectedValues={selectedForms[user] ?? []}
          onChange={(values) => setUsers((prev: { [name: string]: string[] }) => ({ ...prev, [user]: values }))}
        />
      ))
      }
    </>
  );
}

export default function ModalPA({
  openModalPA,
  setOpenModalPA,
  conjunto,
}: props) {
  const { t } = useTranslation('translation');
  const [selectedGestores, setSelectedGestores] = useState<{ [name: string]: string[] }>(conjunto.listaGestoresPA || {});
  const [selectedInspetores, setSelectedInspetores] = useState<{ [name: string]: string[] }>(conjunto.listaInspetoresPA || {});
  const [edit, setEdit] = useState('');

  const results = useQueries({
    queries: [
      {
        queryKey: ['GET_GESTORES'],
        queryFn: async () => {
          const token = await AttToken();
          if (token) {
            const response = await PAService.getListaGestoresPA(token);
            if (!response) throw new Error('erro ao buscar gestores');
            return response;
          }
        },
        retry: 5,
        refetchOnWindowFocus: false,
        refetchOnMount: 'always',
      },
      {
        queryKey: ['GET_INSPETORES'],
        queryFn: async () => {
          const token = await AttToken();
          if (token) {
            const response = await PAService.getListaInspetoresPA(token);
            if (!response) throw new Error('erro ao buscar inspetores');
            return response;
          }
        },
        retry: 5,
        refetchOnWindowFocus: false,
        refetchOnMount: 'always',
      },
      {
        queryKey: ['GET_FORMS'],
        queryFn: async () => {
          const token = await AttToken();
          if (token) {
            const body = { ...token };
            const result = await formService.getSimpleForm(body, true);
            if (!result) throw new Error('erro ao buscar formulários');
            return result;
          }
        },
        retry: 5,
        refetchOnWindowFocus: false,
        refetchOnMount: 'always',
      }
    ]
  });

  const isLoading = results?.some((result) => result.isLoading);
  const isFetching = results?.some((result) => result.isFetching);

  const gestores = results[0].data || [];
  const inspetores = results[1].data || [];
  const forms = results[2].data || [];

  const { mutate, isPending } = useMutation({
    mutationKey: ['SAVE_GESTORES'],
    mutationFn: async () => {
      const promise = toast.promise(async () => {
        const token = await AttToken();
        if (token) {
          const response = await PAService.salvarGestoresPA(token, conjunto?.id, selectedGestores, selectedInspetores);
          if (!response) throw new Error('erro ao salvar');
          return response;
        }
      }, {
        success: t('Services.ServiceModal.changesSaved')
      }, { toastId: 'salvando' });
      return promise;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['GET_PLACES'] });
      setOpenModalPA(false);
    }
  });

  const onLoading = isLoading || isFetching || isPending;

  return (
    <Dialog
      fullWidth
      maxWidth='sm'
      open={openModalPA}
      onClose={() => setOpenModalPA(false)}
      scroll={'paper'}
    >
      <DialogTitle>{t('Services.ServiceModal.actionPlanAssociations')}</DialogTitle>
      {!edit.length ? <IconButton
        onClick={() => setOpenModalPA(false)}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
        : <Button
          sx={() => ({
            position: 'absolute',
            right: 8,
            top: 8,
          })}
          onClick={() => setEdit('')}
          startIcon={<ArrowBackIcon />}>
          {t('aprovadores.voltar')}
        </Button>}
      <DialogContent dividers={true}>
        <Stack
          direction='column'
          justifyContent='center'
          alignItems='stretch'
          spacing={2}
        >
          {!edit.length ? <>
            <Button
              onClick={() => setEdit('gestores')}
              disabled={onLoading}
              endIcon={<BorderColorIcon />}
              variant='outlined'
              fullWidth
              size='large'>
              {t('Services.ServiceModal.managerAssociations')}
            </Button>
            <Button
              onClick={() => setEdit('inspetores')}
              disabled={onLoading}
              endIcon={<BorderColorIcon />}
              variant='outlined'
              fullWidth
              size='large'>
              {t('Services.ServiceModal.inspectorAssociations')}
            </Button>
          </> : edit === 'gestores'
            ? <AssociateForm
              setUsers={setSelectedGestores}
              selectedValues={Object.keys(selectedGestores) ?? []}
              forms={forms ?? []}
              users={gestores ?? []}
              selectedForms={selectedGestores}
              label={t('Services.ServiceModal.addManagers')} />
            : <AssociateForm
              setUsers={setSelectedInspetores}
              selectedValues={Object.keys(selectedInspetores) ?? []}
              forms={forms ?? []}
              users={inspetores ?? []}
              selectedForms={selectedInspetores}
              label={t('Services.ServiceModal.addInspectors')} />}
        </Stack>
      </DialogContent>
      <DialogActions>
        {!edit.length
          && <Button disabled={onLoading}
            onClick={() => mutate()}>
            {t('save')}
          </Button>}
      </DialogActions>
    </Dialog>
  );
}
